import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, Injectable, NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './components/home/home.component';
import { ProfileComponent } from './components/profile/profile.component';
import { BoardUserComponent } from './components/board-user/board-user.component';
import { HeaderComponent } from './components/home/header/header.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { FooterComponent } from './components/footer/footer.component';
import { PhoneMaskDirective } from './util/phone-mask.directive';
import { PhoneVerificationComponent } from './components/phone-verification/phone-verification.component';
import { EmailVerificationComponent } from './components/email-verification/email-verification.component';
import { OrderListComponent } from './components/order/order-list/order-list.component';
import { SortByPipe } from './pipes/sortBy.pipe';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { authInterceptorProviders } from './helpers/auth.interceptor';
import { DatePipe } from '@angular/common';
import { NgxMaskModule } from 'ngx-mask';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { NgbDateParserFormatter, NgbDateStruct, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CalendarDateFormatter, CalendarModule, CalendarNativeDateFormatter, DateAdapter, DateFormatterParams } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { RoomListComponent } from './components/room/room-list/room-list.component';
import { RelayComponent } from './components/relay/relay.component';
import { ngxLoadingAnimationTypes, NgxLoadingModule } from 'ngx-loading';
import { DoorListComponent } from './components/door/door-list/door-list.component';
import { WorkPlaceListComponent } from './components/workplace/workplace-list/workplace-list.component';
import { BoardAdminComponent } from './components/board-admin/board-admin.component';
import { OptionsSectionComponent } from './components/home/options-section/options-section.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CarouselModule } from 'primeng/carousel';
import { ButtonModule } from 'primeng/button';
import { BookComponent } from './components/book/book.component';
import { LoginModalComponent } from './components/login-modal/login-modal.component';
import { ModalContainerComponent } from './components/modal-container/modal-container.component';
import { BookConfirmComponent } from './components/book-confirm/book-confirm.component';
import { PasswordResetTokenComponent } from './components/user/password/reset-token/password-reset-token.component';
import { PasswordResetComponent } from './components/user/password/reset/password-reset.component';
import { ZkUserListComponent } from './components/zk/zk-user-list/zk-user-list.component';
import { ZkUserEditComponent } from './components/zk/zk-user-edit/zk-user-edit.component';
import { UserListComponent } from './components/user/user-list/user-list.component';
import { ConfirmDialogComponent } from './components/dialog/confirm-dialog/confirm-dialog.component';
import { UserEditComponent } from './components/user/user-edit/user-edit.component';
import { WorkPlaceEditComponent } from './components/workplace/workplace-edit/workplace-edit.component';
import { RoomEditComponent } from './components/room/room-edit/room-edit.component';
import { EmailVerifyComponent } from './components/user/email/verify/email-verify.component';
import { TimeperiodListComponent } from './components/timeperiod/timeperiod-list/timeperiod-list.component';
import { TimeperiodEditComponent } from './components/timeperiod/timeperiod-edit/timeperiod-edit.component';
import { BookingPriceListComponent } from './components/prices/booking-prices/booking-price-list/booking-price-list.component';
import { BookingPriceEditComponent } from './components/prices/booking-prices/booking-price-edit/booking-price-edit.component';
import { OfficeEditComponent } from './components/office/office-edit/office-edit.component';
import { OfficeListComponent } from './components/office/office-list/office-list.component';
import { RelayBoardEditComponent } from './components/relay-board/relay-board-edit/relay-board-edit.component';
import { RelayBoardListComponent } from './components/relay-board/relay-board-list/relay-board-list.component';
import { RelayEditComponent } from './components/relay/relay-edit/relay-edit.component';
import { RelayListComponent } from './components/relay/relay-list/relay-list.component';
import { LoginComponent } from './components/login/login.component';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input-gg';
import { DATE_FORMAT_OPTIONS, NgbDateCustomParserFormatter } from './helpers/NgbDateCustomParserFormatter';
import { PhoneUpdateComponent } from './components/user/phone/update/phone-update.component';
import { QrCodeComponent } from './components/qr-code/qr-code.component';
import { AdminOrderListComponent } from './components/board-admin/admin-order-list/admin-order-list.component';
import { RelayScriptRuleListComponent } from './components/relay-script-rule/relay-script-rule-list/relay-script-rule-list.component';
import { RelayScriptRuleEditComponent } from './components/relay-script-rule/relay-script-rule-edit/relay-script-rule-edit.component';
import { UserInfoQrComponent } from './components/user/user-info-qr/user-info-qr.component';
import { UserNotificationListComponent } from './components/user/notification/user-notification-list.component';
import { ZkRtLogComponent } from './components/zk/zk-rt-log/zk-rt-log.component';
import { CouponListComponent } from './components/office-coupons/coupon-list/coupon-list.component';
import { CouponEditComponent } from './components/office-coupons/coupon-edit/coupon-edit.component';
import { DiscountEditComponent } from './components/discount/discount-edit/discount-edit.component';
import { DiscountListComponent } from './components/discount/discount-list/discount-list.component';
import { PolicyManagementComponent } from './components/user/policy-management/policy-management.component';
import { CheckoutSuccessComponent } from './components/checkout-success/checkout-success.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { VisitorsListComponent } from './components/visitors/visitors-list/visitors-list.component';
import { VisitorsEditComponent } from './components/visitors/visitors-edit/visitors-edit.component';
import { VisitorsManagementComponent } from './components/visitors/visitors-management/visitors-management.component';
import { QuickCardListComponent } from './components/quick-card/quick-card-list/quick-card-list.component';
import { CoffeeUseComponent } from './components/coffee/coffee-use/coffee-use.component';
import { CoffeeConfigListComponent } from './components/coffee/coffee-config-list/coffee-config-list.component';
import { CoffeeConfigEditComponent } from './components/coffee/coffee-config-edit/coffee-config-edit.component';
import { CoffeeMakeComponent } from './components/coffee/coffee-make/coffee-make.component';
import { RoomClosureListComponent } from './components/roomclosure/roomclosure-list/roomclosure-list.component';
import { RoomClosureEditComponent } from './components/roomclosure/roomclosure-edit/roomclosure-edit.component';
import { DateTimePickerComponent } from './components/date-time-picker-component/date-time-picker.component';
import { SensorListComponent } from './components/sensor/sensor-list/sensor-list.component';
import { SensorEditComponent } from './components/sensor/sensor-edit/sensor-edit.component';
import { SubscriptionEditComponent } from './components/subscription/subscription-edit/subscription-edit.component';
import { LessonsScheduleListComponent } from './components/lessons-schedule/lessons-schedule-list/lessons-schedule-list.component';
import { LessonsScheduleEditComponent } from './components/lessons-schedule/lessons-schedule-edit/lessons-schedule-edit.component';
import { SubscriptionListComponent } from './components/subscription/subscription-list/subscription-list.component';
import { UserSubscriptionsInfoComponent } from './components/user/user-subscriptions-info/user-subscriptions-info.component';
import { UserChildrenInfoComponent } from 'src/app/components/user/user-children-info/user-children-info.component';
import { BookedLessonsListComponent } from './components/booked-lessons/booked-lessons-list/booked-lessons-list.component';
import {
  SubscriptionCouponsEditComponent
} from 'src/app/components/subscription-coupons/subscription-coupons-edit/subscription-coupons-edit.component';
import {
  SubscriptionCouponsListComponent
} from 'src/app/components/subscription-coupons/subscription-coupons-list/subscription-coupons-list.component';
import {
  ScheduleCalendarComponent
} from './components/lessons-schedule/lessons-schedule-list/schedule-calendar/schedule-calendar.component';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { UserAddSubscriptionComponent } from 'src/app/components/user/user-add-subscription/user-add-subscription.component';
import { BookedLessonAddModal } from './components/booked-lessons/booked-lessons-add-modal/booked-lesson-add-modal.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { SortableHeader } from 'src/app/components/user/user-list/Sortable';
import { CoursesListComponent } from 'src/app/components/lessons-schedule/lessons-schedule-list/courses-list/courses-list.component';
import {
  AddEditCourseModalComponent
} from './components/lessons-schedule/lessons-schedule-list/courses-list/add-edit-course-modal/add-edit-course-modal.component';
import { SecureStylePipe } from 'src/app/pipes/secure-style.pipe';
import {
  RescheduleBookedLessonComponent
} from 'src/app/components/booked-lessons/reschedule-booked-lesson/reschedule-booked-lesson.component';

@Injectable()
class CustomDateFormatter extends CalendarNativeDateFormatter {

  public dayViewHour({ date, locale }: DateFormatterParams): string {
    return new Intl.DateTimeFormat('ca', {
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  }
}

// Factory function required during AOT compilation
export function httpTranslateLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {

  parse(value: string): NgbDateStruct | null {
    if (!value) {
      return null;
    }
    const parts = value.trim().split('.');
    if (parts.length !== 3) {
      return null;
    }

    const day = +parts[0];
    const month = +parts[1];
    const year = +parts[2];
    return { day, month, year };
  }

  format(date: NgbDateStruct | null): string {
    if (!date) {
      return '';
    }
    const day = String(date.day).padStart(2, '0');
    const month = String(date.month).padStart(2, '0');
    const year = String(date.year);
    return `${day}.${month}.${year}`;
  }
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    ProfileComponent,
    BoardUserComponent,
    BoardAdminComponent,
    HeaderComponent,
    NavigationComponent,
    OptionsSectionComponent,
    FooterComponent,
    PhoneMaskDirective,
    PhoneVerificationComponent,
    EmailVerificationComponent,
    EmailVerifyComponent,
    OrderListComponent,
    SortByPipe,
    SecureStylePipe,
    RoomListComponent,
    RoomEditComponent,
    DoorListComponent,
    WorkPlaceListComponent,
    WorkPlaceEditComponent,
    RelayComponent,
    BookComponent,
    BookConfirmComponent,
    LoginModalComponent,
    ModalContainerComponent,
    PasswordResetTokenComponent,
    PasswordResetComponent,
    CheckoutSuccessComponent,
    ZkUserListComponent,
    ZkUserEditComponent,
    UserListComponent,
    UserEditComponent,
    ConfirmDialogComponent,
    TimeperiodListComponent,
    TimeperiodEditComponent,
    BookingPriceListComponent,
    BookingPriceEditComponent,
    OfficeListComponent,
    OfficeEditComponent,
    RelayBoardListComponent,
    RelayBoardEditComponent,
    RelayListComponent,
    RelayEditComponent,
    LoginComponent,
    PhoneUpdateComponent,
    QrCodeComponent,
    AdminOrderListComponent,
    RelayScriptRuleListComponent,
    RelayScriptRuleEditComponent,
    UserInfoQrComponent,
    UserNotificationListComponent,
    ZkRtLogComponent,
    CouponListComponent,
    CouponEditComponent,
    PolicyManagementComponent,
    VisitorsListComponent,
    VisitorsEditComponent,
    OrderListComponent,
    VisitorsEditComponent,
    VisitorsManagementComponent,
    PolicyManagementComponent,
    QuickCardListComponent,
    CoffeeUseComponent,
    CoffeeConfigListComponent,
    CoffeeConfigEditComponent,
    CoffeeMakeComponent,
    RoomClosureListComponent,
    RoomClosureEditComponent,
    DateTimePickerComponent,
    SensorListComponent,
    SensorEditComponent,
    DiscountEditComponent,
    DiscountListComponent,
    SubscriptionEditComponent,
    SubscriptionListComponent,
    LessonsScheduleListComponent,
    LessonsScheduleEditComponent,
    BookedLessonAddModal,
    SensorListComponent,
    UserSubscriptionsInfoComponent,
    UserChildrenInfoComponent,
    BookedLessonsListComponent,
    RescheduleBookedLessonComponent,
    SubscriptionCouponsListComponent,
    SubscriptionCouponsEditComponent,
    ScheduleCalendarComponent,
    UserAddSubscriptionComponent,
    SortableHeader,
    CoursesListComponent,
    AddEditCourseModalComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    NgxPageScrollModule,
    NgxPageScrollCoreModule,
    NgbModule,
    CarouselModule,
    ButtonModule,
    NgSelectModule,
    NgxIntlTelInputModule,
    NgxMaskModule.forRoot(),
    NgxLoadingModule.forRoot({
      animationType: ngxLoadingAnimationTypes.circle,
      backdropBackgroundColour: 'rgba(0,0,0,0.1)',
      backdropBorderRadius: '4px',
      primaryColour: '#ffffff',
      secondaryColour: '#ffffff',
      tertiaryColour: '#ffffff',
    }),
    CalendarModule.forRoot({
      provide: DateAdapter, useFactory: adapterFactory
    }, {
      dateFormatter: {
        provide: CalendarDateFormatter,
        useClass: CustomDateFormatter
      }
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgxPaginationModule,
    ButtonsModule
  ],
  exports: [
    PhoneMaskDirective,
  ],
  providers: [
    authInterceptorProviders,
    DatePipe,
    SortByPipe,
    {
      provide: DATE_FORMAT_OPTIONS,
      useValue: {
        format: 'yyyy-mm-dd',
        DELIMITER: '-'
      }
    },
    {
      provide: NgbDateParserFormatter,
      useClass: NgbDateCustomParserFormatter
    }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  entryComponents: [
    LoginModalComponent,
    VisitorsEditComponent,
  ]
})
export class AppModule {
}
